import { apiClient } from './client'
import type { AiProposalView, AiReviewView, AiSummaryView } from '../types/ai'

/**
 * Typed fetch wrappers for `module.ai`'s IB-172 engines (Summary Generator, Assessor) — same
 * thin-wrapper convention as `api/film.ts`.
 *
 * GUARDED-WRITE CONTRACT: this file never writes a guarded field directly. {@link acceptProposal}
 * POSTs `{comment}` to `/ai-proposals/{id}/accept` — the server-side guarded write (if any) happens
 * entirely behind that one endpoint (`module.film`'s `SpendAdjustmentService` via `module.ai`'s
 * `AiProposalService`), never a raw `qualifyingPct`/`inState`/`locked` request body from this file.
 */

interface CursorPage<T> {
  items: T[]
  nextCursor: string | null
}

export async function generateSummary(targetType: string, targetId: string): Promise<AiSummaryView> {
  const { data } = await apiClient.post<AiSummaryView>('/ai-summaries', { targetType, targetId })
  return data
}

export async function listSummaries(targetType: string, targetId: string): Promise<AiSummaryView[]> {
  const { data } = await apiClient.get<CursorPage<AiSummaryView>>(
    `/ai-summaries?target_type=${encodeURIComponent(targetType)}&target_id=${encodeURIComponent(targetId)}`,
  )
  return data.items
}

export async function generateProposal(
  targetType: string,
  targetId: string,
  fieldName: string,
): Promise<AiProposalView> {
  const { data } = await apiClient.post<AiProposalView>('/ai-proposals', { targetType, targetId, fieldName })
  return data
}

export async function listProposals(targetId: string): Promise<AiProposalView[]> {
  const { data } = await apiClient.get<CursorPage<AiProposalView>>(
    `/ai-proposals?target_id=${encodeURIComponent(targetId)}`,
  )
  return data.items
}

/**
 * THE human "dispose" half of BR-1102 ("AI proposes, human disposes") — accepts a pending
 * proposal. The actual guarded write, if any, happens entirely server-side; this call never
 * carries the guarded field's name or value itself, only `comment`.
 */
export async function acceptProposal(id: string, comment: string): Promise<AiProposalView> {
  const { data } = await apiClient.post<AiProposalView>(`/ai-proposals/${id}/accept`, { comment })
  return data
}

export async function rejectProposal(id: string, comment: string): Promise<AiProposalView> {
  const { data } = await apiClient.post<AiProposalView>(`/ai-proposals/${id}/reject`, { comment })
  return data
}

export interface ListAiReviewsParams {
  cursor?: string | null
  limit?: number
}

/**
 * `GET /ai-reviews` (IB-282 View 4, AC4.2, now gated `ai_review:R`) — the ops dashboard's
 * "AI-accept rate" tile walks this bounded, keyset-paged list client-side (design §2, ≤10
 * pages/2,000 rows per window — `useOperationsAnalytics`'s own bound, not enforced here).
 */
export async function listAiReviews(
  params: ListAiReviewsParams = {},
  signal?: AbortSignal,
): Promise<CursorPage<AiReviewView>> {
  const q = new URLSearchParams()
  if (params.cursor) q.set('cursor', params.cursor)
  q.set('limit', String(params.limit ?? 50))
  const { data } = await apiClient.get<CursorPage<AiReviewView>>(`/ai-reviews?${q.toString()}`, { signal })
  return data
}
